import { Instagram, Facebook, Linkedin, Mail } from "lucide-react";
import SEO from "@/components/SEO";
import ContactForm from "@/components/ContactForm";
import { SOCIAL_LINKS, BRAND } from "@/constants/brand";
import { useTranslation } from "@/i18n/LanguageContext";

// TikTok icon (lucide-react doesn't ship one - inline SVG)
function TikTokIcon({ size = 18 }) {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            width={size}
            height={size}
            viewBox="0 0 24 24"
            fill="currentColor"
            aria-hidden="true"
        >
            <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43V9.71a8.16 8.16 0 0 0 4.77 1.52V7.79a4.85 4.85 0 0 1-1.84-1.1Z" />
        </svg>
    );
}

export default function ContactPage() {
    const { t } = useTranslation();
    return (
        <div data-testid="contact-page">
            <SEO
                title={t("contact.seo.title")}
                description={t("contact.seo.description")}
                keywords="contact, women-owned home services directory, get in touch, trustherhome"
                path="/contact"
            />

            <section className="bg-cream">
                <div className="container-x pt-20 lg:pt-28 pb-12 max-w-4xl">
                    <p className="label-eyebrow">{t("contact.hero.eyebrow")}</p>
                    <h1 className="mt-5 text-4xl sm:text-5xl lg:text-6xl font-heading tracking-tight leading-[1.05] text-ink">
                        {t("contact.hero.title1")}{" "}
                        <span className="italic text-clay">{t("contact.hero.titleAccent")}</span>
                    </h1>
                    <p className="mt-6 text-lg text-ink-soft leading-relaxed">
                        {t("contact.hero.body")}
                    </p>
                </div>
            </section>

            <section className="bg-cream pb-24">
                <div className="container-x grid lg:grid-cols-12 gap-12">
                    <div className="lg:col-span-5">
                        <div className="bg-cream-deep border border-border rounded-3xl p-8 lg:p-10 space-y-8">
                            <div>
                                <p className="label-eyebrow">{t("contact.info.emailLabel")}</p>
                                <div className="mt-3 flex items-center gap-3 text-ink">
                                    <span className="w-10 h-10 rounded-full bg-clay/30 flex items-center justify-center text-ink">
                                        <Mail size={18} />
                                    </span>
                                    <a
                                        href={`mailto:${BRAND.email}`}
                                        data-testid="contact-email-link"
                                        className="text-ink font-medium link-underline font-email"
                                    >
                                        {BRAND.email}
                                    </a>
                                </div>
                            </div>

                            <div>
                                <p className="label-eyebrow">{t("contact.info.followLabel")}</p>
                                <div className="mt-4 flex items-center gap-3 flex-wrap">
                                    <a
                                        href={SOCIAL_LINKS.instagram}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        data-testid="contact-instagram"
                                        aria-label="Instagram"
                                        className="w-11 h-11 rounded-full border border-border bg-cream flex items-center justify-center text-ink hover:bg-ink hover:text-cream hover:border-ink transition-colors"
                                    >
                                        <Instagram size={18} />
                                    </a>
                                    <a
                                        href={SOCIAL_LINKS.facebook}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        data-testid="contact-facebook"
                                        aria-label="Facebook"
                                        className="w-11 h-11 rounded-full border border-border bg-cream flex items-center justify-center text-ink hover:bg-ink hover:text-cream hover:border-ink transition-colors"
                                    >
                                        <Facebook size={18} />
                                    </a>
                                    <a
                                        href={SOCIAL_LINKS.tiktok}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        data-testid="contact-tiktok"
                                        aria-label="TikTok"
                                        className="w-11 h-11 rounded-full border border-border bg-cream flex items-center justify-center text-ink hover:bg-ink hover:text-cream hover:border-ink transition-colors"
                                    >
                                        <TikTokIcon size={18} />
                                    </a>
                                    <a
                                        href={SOCIAL_LINKS.linkedin}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        data-testid="contact-linkedin"
                                        aria-label="LinkedIn"
                                        className="w-11 h-11 rounded-full border border-border bg-cream flex items-center justify-center text-ink hover:bg-ink hover:text-cream hover:border-ink transition-colors"
                                    >
                                        <Linkedin size={18} />
                                    </a>
                                </div>
                            </div>

                            <div className="pt-6 border-t border-border">
                                <p className="font-heading text-xl text-ink">
                                    {t("contact.info.launchingTitle")}
                                </p>
                                <p className="mt-2 text-ink-soft leading-relaxed">
                                    {t("contact.info.launchingBody")}
                                </p>
                            </div>
                        </div>
                    </div>

                    <div className="lg:col-span-7">
                        <ContactForm />
                    </div>
                </div>
            </section>
        </div>
    );
}
